import type { FacebookConfig } from '@habity/types';
import type ReactPixelType from 'react-facebook-pixel';

import { isDev } from '~/lib/utils';

// Singleton state
let isInitialized = false;
let initPromise: Promise<boolean> | null = null;
let currentPixelId: string | null = null;
let ReactPixel: typeof ReactPixelType | null = null;

export const useFacebook = () => {
  const showTrackingLogs =
    isDev && process.env.NEXT_PUBLIC_SHOW_TRACKING_LOGS !== 'false';

  const init = async (config: FacebookConfig | string) => {
    // Only run in browser environment
    if (typeof window === 'undefined') return false;

    const pixelId = typeof config === 'string' ? config : config.pixelId;

    if (!pixelId) {
      console.error('Facebook Pixel ID is required');
      return false;
    }

    // If already initialized with the same pixel ID, return early
    if (isInitialized && currentPixelId === pixelId) {
      return true;
    }

    // If initialization is in progress, wait for it to complete
    if (initPromise) {
      return await initPromise;
    }

    if (showTrackingLogs) {
      console.log('Initializing Facebook Pixel');
    }

    // Create a promise that will resolve when initialization is complete
    initPromise = new Promise(async (resolve) => {
      try {
        // Dynamically import react-facebook-pixel
        const fbPixelModule = await import('react-facebook-pixel');
        ReactPixel = fbPixelModule.default;

        // Initialize the pixel
        ReactPixel.init(pixelId, undefined, {
          autoConfig: true,
          debug: showTrackingLogs,
        });

        // Track initial page view
        ReactPixel.pageView();

        isInitialized = true;
        currentPixelId = pixelId;

        if (showTrackingLogs) {
          console.log('Facebook Pixel loaded successfully');
        }

        resolve(true);
      } catch (error) {
        console.error('Error initializing Facebook Pixel:', error);
        initPromise = null;
        resolve(false);
      }
    });

    return await initPromise;
  };

  const track = async (
    eventName: string,
    properties?: Record<string, unknown>
  ) => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // If initialization is in progress, wait for it to complete
    if (initPromise && !isInitialized) {
      try {
        await initPromise;
      } catch (error) {
        console.error(
          'Error waiting for Facebook Pixel initialization:',
          error
        );
        return;
      }
    }

    // If still not initialized after waiting, return early
    if (!isInitialized || !ReactPixel) {
      if (showTrackingLogs) {
        console.warn(
          'Facebook Pixel not initialized, skipping event:',
          eventName
        );
      }
      return;
    }

    try {
      if (showTrackingLogs) {
        console.log('Tracking in Facebook Pixel', eventName, properties);
      }

      ReactPixel.track(eventName, properties);
    } catch (error) {
      console.error('Error tracking event in Facebook Pixel:', error);
    }
  };

  const trackCustom = async (
    eventName: string,
    properties?: Record<string, unknown>
  ) => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // If initialization is in progress, wait for it to complete
    if (initPromise && !isInitialized) {
      try {
        await initPromise;
      } catch (error) {
        console.error(
          'Error waiting for Facebook Pixel initialization:',
          error
        );
        return;
      }
    }

    // If still not initialized after waiting, return early
    if (!isInitialized || !ReactPixel) {
      if (showTrackingLogs) {
        console.warn(
          'Facebook Pixel not initialized, skipping custom event:',
          eventName
        );
      }
      return;
    }

    try {
      if (showTrackingLogs) {
        console.log(
          'Tracking custom event in Facebook Pixel',
          eventName,
          properties
        );
      }

      ReactPixel.trackCustom(eventName, properties);
    } catch (error) {
      console.error('Error tracking custom event in Facebook Pixel:', error);
    }
  };

  const pageView = async () => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // If initialization is in progress, wait for it to complete
    if (initPromise && !isInitialized) {
      try {
        await initPromise;
      } catch (error) {
        console.error(
          'Error waiting for Facebook Pixel initialization:',
          error
        );
        return;
      }
    }

    if (!isInitialized || !ReactPixel) return;

    try {
      if (showTrackingLogs) {
        console.log('Tracking PageView in Facebook Pixel');
      }
      ReactPixel.pageView();
    } catch (error) {
      console.error('Error tracking PageView in Facebook Pixel:', error);
    }
  };

  return {
    init,
    track,
    trackCustom,
    pageView,
  };
};
